(() => {
  'use strict';

  const cardSelector = 'li.product-grid__item';
  const linkSelector = 'a.product-card__link, a.contents';

  const getProductUrl = (card) => {
    const link = card.querySelector(linkSelector);
    if (!link) return null;
    const url = new URL(link.getAttribute('href'), window.location.origin);
    url.search = '';
    url.hash = '';
    return url;
  };

  const updateCard = (card, productUrl, variant, fallbackImage) => {
    const rawImageUrl = variant.featured_image?.src || fallbackImage;
    const imageUrl = rawImageUrl ? rawImageUrl.replace(/^\/\//, 'https://') : null;
    const variantUrl = new URL(productUrl.href);
    variantUrl.searchParams.set('variant', variant.id);

    card.querySelectorAll(linkSelector).forEach((link) => {
      link.setAttribute('href', variantUrl.toString());
    });

    if (imageUrl) {
      card.querySelectorAll('.card-gallery img, .product-media-container img').forEach((image) => {
        image.src = imageUrl;
        image.srcset = `${imageUrl} 1x`;
        image.removeAttribute('data-src');
        image.removeAttribute('data-srcset');
        image.loading = 'eager';
        image.style.opacity = '1';
        const loader = image.closest('.solo-image-loader');
        if (loader) {
          loader.classList.add('solo-image-loader--loaded', 'solo-image-loader--disabled');
          loader.dataset.soloLoaderToken = String(Date.now());
          window.setTimeout(() => {
            loader.classList.add('solo-image-loader--loaded', 'solo-image-loader--disabled');
          }, 150);
        }
      });
      card.querySelectorAll('.card-gallery source, .product-media-container source').forEach((source) => {
        source.srcset = imageUrl;
        source.removeAttribute('data-srcset');
      });
    }

  };

  const colorToCss = (value) => {
    const color = String(value || '').trim().toLowerCase();
    const colors = {
      black: '#111111', noir: '#111111', gris: '#9b9b9b', grey: '#9b9b9b', gray: '#9b9b9b',
      white: '#ffffff', blanc: '#ffffff', beige: '#d8c4a2', brown: '#7b4b2a', marron: '#7b4b2a',
      red: '#c62828', rouge: '#c62828', blue: '#1e5aa8', bleu: '#1e5aa8', green: '#2f7d4a',
      vert: '#2f7d4a', yellow: '#e5b52f', jaune: '#e5b52f', pink: '#e99aaa', rose: '#e99aaa',
      orange: '#e87522', purple: '#70459b', violet: '#70459b', gold: '#c9a227', or: '#c9a227',
      silver: '#b9bec5', argent: '#b9bec5'
    };
    return colors[color] || '#d5d5d5';
  };

  const renderColorSwatches = (card, productUrl, colorVariants) => {
    card.querySelector('.solo-color-swatches')?.remove();
    if (!colorVariants.length) return;
    const wrapper = document.createElement('div');
    wrapper.className = 'solo-color-swatches';
    wrapper.setAttribute('aria-label', 'Choisir une couleur');
    colorVariants.forEach((variant) => {
      const label = variant.__soloColorLabel || variant.title;
      const href = new URL(productUrl.href);
      href.searchParams.set('variant', variant.id);
      const swatch = document.createElement('a');
      swatch.className = 'solo-color-swatch';
      swatch.href = href.toString();
      swatch.title = label;
      swatch.setAttribute('aria-label', label);
      swatch.style.backgroundColor = colorToCss(label);
      if (/white|blanc|beige|silver|argent/i.test(label)) swatch.classList.add('solo-color-swatch--light');
      wrapper.appendChild(swatch);
    });
    card.appendChild(wrapper);
  };

  const expandCard = async (source) => {
    if (source.dataset.soloVariantsExpanded === 'true' || source.dataset.soloVariantsLoading === 'true') return;
    const productUrl = getProductUrl(source);
    if (!productUrl) return;

    source.dataset.soloVariantsLoading = 'true';
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(`${productUrl.pathname}.js`, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error(`Product JSON ${response.status}`);
      const product = await response.json();
      const variants = product.variants || [];
      const fallbackImage = product.featured_image || product.images?.[0];
      if (!variants.length) return;

      // Show one card per color, not one card per size. Shopify exposes the
      // option names on product.options and the selected values on variant.options.
      const colorOptionIndex = (product.options || []).findIndex((option) => {
        const name = typeof option === 'string' ? option : option.name;
        return /color|colour|couleur|لون/i.test(name || '');
      });
      if (colorOptionIndex < 0) {
        source.dataset.soloVariantsExpanded = 'true';
        return;
      }
      const resolvedColorIndex = colorOptionIndex;
      const colorGroups = new Map();
      variants.forEach((variant) => {
        const color = variant.options?.[resolvedColorIndex] || variant.option1 || variant.title;
        const key = String(color || 'Default Title').trim().toLowerCase();
        const current = colorGroups.get(key);
        if (!current || (!current.available && variant.available)) {
          variant.__soloColorLabel = color;
          colorGroups.set(key, variant);
        }
      });
      const colorVariants = Array.from(colorGroups.values());

      // Do not show a lone gray dot: swatches are useful only when there are
      // at least two distinct colors to choose from.
      if (colorVariants.length < 2) {
        source.dataset.soloVariantsExpanded = 'true';
        return;
      }
      renderColorSwatches(source, productUrl, colorVariants);
      source.dataset.soloVariantsExpanded = 'true';
    } catch (error) {
      // Keep the normal Shopify product card if the product JSON is unavailable.
      source.dataset.soloVariantsExpanded = 'error';
    } finally {
      window.clearTimeout(timeout);
      delete source.dataset.soloVariantsLoading;
    }
  };

  const scan = (root = document) => {
    if (root instanceof Element && root.matches(cardSelector) && !root.dataset.soloVariantCard) {
      expandCard(root);
    }
    root.querySelectorAll(cardSelector).forEach((card) => {
      if (!card.dataset.soloVariantCard) expandCard(card);
    });
  };

  const style = document.createElement('style');
  style.textContent = '.product-grid__item:has(.solo-color-swatches){height:auto!important;min-height:0;align-self:start;overflow:visible}.solo-color-swatches{position:relative;z-index:3;display:flex;align-items:center;flex-wrap:wrap;clear:both;width:100%;min-height:36px;box-sizing:border-box;gap:7px;margin:0 0 10px;padding:7px 8px 10px;background:var(--color-background,#fff)}.solo-color-swatch{display:inline-block;flex:0 0 19px;width:19px;height:19px;border-radius:50%;border:1px solid rgba(0,0,0,.18);box-shadow:0 0 0 1px rgba(255,255,255,.8) inset;transition:transform .15s ease,box-shadow .15s ease}.solo-color-swatch:hover,.solo-color-swatch:focus-visible{position:relative;z-index:4;transform:scale(1.14);box-shadow:0 0 0 2px #111}.solo-color-swatch--light{border-color:rgba(0,0,0,.4)}';
  document.head.appendChild(style);

  scan();
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) scan(node);
    }));
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
